import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  IconButton,
  Chip,
  Card,
  CardContent,
  Grid,
  Avatar
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useTranslation } from "react-i18next";
import { useSnackbar } from 'notistack';
import YearSelector from '../../../components/Common/YearSelector';
import { fetchJson } from '../../../utils/fetchJson';

export default function ClassPromotionSummaryPage() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [summary, setSummary] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [execBusy, setExecBusy] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [nextYearId, setNextYearId] = useState(null);
  const [nextClassId, setNextClassId] = useState('');
  const [tabIndex, setTabIndex] = useState(0);
  const [progressionMap, setProgressionMap] = useState({}); // { [classId]: nextClassId }
  const [mapSaving, setMapSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [overrideStudent, setOverrideStudent] = useState(null);
  const [overrideBusy, setOverrideBusy] = useState(false);
  const [holdStudent, setHoldStudent] = useState(null);

  // Hold a student back. The mirror of promoting anyway, and the mechanism
  // nursery relies on: its scale has no failure band, so the teacher decides.
  const handleHoldBack = async () => {
    if (!holdStudent) return;
    setOverrideBusy(true);
    try {
      await fetchJson(`/api/promotion/student/${holdStudent.studentId}/retain`, {
        method: 'POST',
        body: { nextAcademicYearId: Number(nextYearId) },
      });
      enqueueSnackbar(
        t('classPromotionSummaryPage.holdDone', {
          name: holdStudent.studentName,
          defaultValue: `${holdStudent.studentName} will repeat the year.`,
        }),
        { variant: 'success' }
      );
      setHoldStudent(null);
      handleCheck();
    } catch (e) {
      enqueueSnackbar(
        e?.userMessage || e?.data?.error || t('classPromotionSummaryPage.holdFailed', { defaultValue: 'Could not hold this student back.' }),
        { variant: 'error' }
      );
      setHoldStudent(null);
    } finally {
      setOverrideBusy(false);
    }
  };

  // Promote one student despite the verdict. Does not re-judge them: the point
  // is to proceed anyway. The server records it against the current user with
  // the reasons they failed, because this is the promotion someone asks about
  // later.
  const handlePromoteAnyway = async () => {
    if (!overrideStudent) return;
    setOverrideBusy(true);
    try {
      const body = { nextAcademicYearId: Number(nextYearId) };
      const mapped = progressionMap?.[selectedClass] ? Number(progressionMap[selectedClass]) : null;
      if (mapped) body.nextClassId = mapped;
      await fetchJson(`/api/promotion/student/${overrideStudent.studentId}/promote`, { method: 'POST', body });
      enqueueSnackbar(
        t('classPromotionSummaryPage.overrideDone', {
          name: overrideStudent.studentName,
          defaultValue: `${overrideStudent.studentName} promoted despite the rules.`,
        }),
        { variant: 'success' }
      );
      setOverrideStudent(null);
      handleCheck();
    } catch (e) {
      enqueueSnackbar(
        e?.userMessage || e?.data?.error || t('classPromotionSummaryPage.overrideFailed', { defaultValue: 'Could not promote this student.' }),
        { variant: 'error' }
      );
      setOverrideStudent(null);
    } finally {
      setOverrideBusy(false);
    }
  };
  const [expandedRows, setExpandedRows] = useState({});

  // Loaders
  const loadClasses = useCallback(async () => {
    try {
      const data = await fetchJson('/api/promotion/classes', { method: 'GET' });
      setClasses(data);
    } catch (err) {
      enqueueSnackbar(err.message || t('classPromotionSummaryPage.errors.loadClasses'), { variant: 'error' });
      setError(err.message);
    }
  }, [enqueueSnackbar]);

  const loadMappings = useCallback(async () => {
    try {
      const data = await fetchJson('/api/promotion/progressions', { method: 'GET' });
      if (data?.mappings) setProgressionMap(data.mappings);
    } catch (err) {
      enqueueSnackbar(t('classPromotionSummaryPage.errors.loadMappings'), { variant: 'warning' });
    }
  }, [enqueueSnackbar]);

  // Initial load
  useEffect(() => {
    loadClasses();
    loadMappings();
  }, [loadClasses, loadMappings]);

  // Auto-refresh on phase/academic year change
  useEffect(() => {
    const onYear = () => {
      loadClasses();
      loadMappings();
      if (selectedClass) {
        // Refresh summary/preview if a class is selected
        handleCheck();
        if (preview) handlePreview();
      }
    };
    const onPhase = onYear;
    window.addEventListener('app:ay:changed', onYear);
    window.addEventListener('app:phase:changed', onPhase);
    return () => {
      window.removeEventListener('app:ay:changed', onYear);
      window.removeEventListener('app:phase:changed', onPhase);
    };
  }, [loadClasses, loadMappings, selectedClass, preview]);

  const handleCheck = async () => {
    if (!selectedClass) {
      setError(t('classPromotionSummaryPage.errors.selectClass'));
      return;
    }
    setError('');
    setLoading(true);
    try {
      const data = await fetchJson(`/api/promotion/class/${selectedClass}`, { method: 'GET' });
      setSummary(data);
      setPreview(null);
    } catch (err) {
      enqueueSnackbar(err.message || t('classPromotionSummaryPage.errors.loadSummary'), { variant: 'error' });
      setError(err.message);
      setSummary(null);
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenConfirm = () => {
    if (!selectedClass) {
      setError(t('classPromotionSummaryPage.errors.selectClass'));
      return;
    }
    if (!nextYearId) {
      setError(t('classPromotionSummaryPage.errors.selectNextYear'));
      return;
    }
    setError('');
    setConfirmOpen(true);
  };
  const handleCloseConfirm = () => setConfirmOpen(false);
  const handleConfirmExecute = async () => {
    await handleExecute();
    setConfirmOpen(false);
  };

  // Progression mapping handlers
  const handleMapChange = (classId, targetId) => {
    setProgressionMap(prev => ({ ...prev, [classId]: targetId }));
  };
  const handleSaveMapping = async () => {
    setMapSaving(true);
    try {
      await fetchJson('/api/promotion/progressions', { method: 'PUT', body: { mappings: progressionMap } });
      enqueueSnackbar(t('classPromotionSummaryPage.snack.mappingsSaved'), { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(e.message || t('classPromotionSummaryPage.errors.saveMappings'), { variant: 'error' });
      setError(e.message);
    } finally {
      setMapSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!selectedClass) {
      setError(t('classPromotionSummaryPage.errors.selectClass'));
      return;
    }
    setError('');
    setPreviewBusy(true);
    try {
      const data = await fetchJson(`/api/promotion/class/${selectedClass}/preview`, { method: 'POST', body: {} });
      setPreview(data);
    } catch (err) {
      enqueueSnackbar(err.message || t('classPromotionSummaryPage.errors.generatePreview'), { variant: 'error' });
      setError(err.message);
      setPreview(null);
    } finally {
      setPreviewBusy(false);
    }
  };

  const handleExecute = async () => {
    if (!selectedClass) {
      setError(t('classPromotionSummaryPage.errors.selectClass'));
      enqueueSnackbar(t('classPromotionSummaryPage.errors.selectClass'), { variant: 'warning' });
      return;
    }
    if (!nextYearId) {
      setError(t('classPromotionSummaryPage.errors.selectNextYear'));
      enqueueSnackbar(t('classPromotionSummaryPage.errors.selectNextYear'), { variant: 'warning' });
      return;
    }
    setError('');
    setExecBusy(true);
    try {
      const body = { nextAcademicYearId: Number(nextYearId) };
      const mapped = progressionMap && progressionMap[selectedClass] ? Number(progressionMap[selectedClass]) : (nextClassId ? Number(nextClassId) : undefined);
      if (mapped) body.progressionMap = { [selectedClass]: mapped };
      const result = await fetchJson(`/api/promotion/class/${selectedClass}/execute`, { method: 'POST', body });
      enqueueSnackbar(
        t('classPromotionSummaryPage.snack.executed', { promoted: result.stats?.promoted || 0, retained: result.stats?.retained || 0 }),
        { variant: 'success' }
      );
      // After execution, refresh summary for visibility
      setPreview(null);
      await handleCheck();
    } catch (err) {
      enqueueSnackbar(err.message || t('classPromotionSummaryPage.errors.executePromotion'), { variant: 'error' });
      setError(err.message);
    } finally {
      setExecBusy(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" gutterBottom>
        {t('classPromotionSummaryPage.title')}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} sx={{ mb: 2 }}>
        <Tab label={t('classPromotionSummaryPage.tabs.mapping')} />
        <Tab label={t('classPromotionSummaryPage.tabs.summaryExecute')} />
      </Tabs>

      {tabIndex === 1 && (
      <>
      <FormControl sx={{ minWidth: 300, mb: 2, mr: 2 }}>
        <InputLabel id="class-select-label">{t('classPromotionSummaryPage.selectClass')}</InputLabel>
        <Select
          labelId="class-select-label"
          value={selectedClass}
          label={t('classPromotionSummaryPage.selectClass')}
          onChange={(e) => setSelectedClass(e.target.value)}
        >
          {classes.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', mb: 2 }}>
        <Typography sx={{ mr: 1 }}>{t('classPromotionSummaryPage.nextAcademicYear')}:</Typography>
        <YearSelector size="small" label={t('classPromotionSummaryPage.nextAcademicYear')} onChange={(id) => setNextYearId(id)} />
      </Box>

      <Box>
        <Button variant="contained" onClick={handleCheck} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : t('classPromotionSummaryPage.loadSummary')}
        </Button>
        <Button sx={{ ml: 1 }} variant="outlined" onClick={handlePreview} disabled={previewBusy || !selectedClass}>
          {previewBusy ? <CircularProgress size={20} /> : t('classPromotionSummaryPage.previewPromotions')}
        </Button>
      </Box>

      {summary && (
        <Paper sx={{ mt: 3, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t('classPromotionSummaryPage.promotionStatus', { className: summary.className })}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>{t('classPromotionSummaryPage.totalStudents')}</Typography>
                  <Typography variant="h4">{summary.stats?.total || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: 'success.light' }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>{t('classPromotionSummaryPage.promoted')}</Typography>
                  <Typography variant="h4">{summary.stats?.promoted || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: 'error.light' }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>{t('classPromotionSummaryPage.retained')}</Typography>
                  <Typography variant="h4">{summary.stats?.retained || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={50} />
                  <TableCell>{t('classPromotionSummaryPage.columns.student')}</TableCell>
                  <TableCell>{t('classPromotionSummaryPage.columns.term1')}</TableCell>
                  <TableCell>{t('classPromotionSummaryPage.columns.term2')}</TableCell>
                  <TableCell>{t('classPromotionSummaryPage.columns.term3')}</TableCell>
                  <TableCell>{t('classPromotionSummaryPage.columns.overall')}</TableCell>
                  <TableCell>{t('classPromotionSummaryPage.columns.status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.summary.map((s) => (
                  <React.Fragment key={s.studentId}>
                    <TableRow sx={{ '& > *': { borderBottom: expandedRows[s.studentId] ? 'unset !important' : undefined } }}>
                      <TableCell>
                        {s.reasons && s.reasons.length > 0 && (
                          <IconButton
                            size="small"
                            onClick={() => setExpandedRows(prev => ({ ...prev, [s.studentId]: !prev[s.studentId] }))}
                          >
                            {expandedRows[s.studentId] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar src={s.photoUrl || ''} sx={{ width: 36, height: 36 }}>
                            {(s.studentName || '').charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography variant="body2">{s.studentName}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{s.termAverages[1]?.toFixed(2) ?? '0.00'}</TableCell>
                      <TableCell>{s.termAverages[2]?.toFixed(2) ?? '0.00'}</TableCell>
                      <TableCell>{s.termAverages[3]?.toFixed(2) ?? '0.00'}</TableCell>
                      <TableCell><strong>{s.overallAverage?.toFixed(2)}</strong></TableCell>
                      <TableCell>
                        {/* The recorded outcome, flagged when it no longer
                            agrees with the criteria — not relabelled, which
                            previously showed "override" on a retained student. */}
                        {s.differsFromRules ? (
                          <Chip
                            icon={s.promoted ? <CheckCircleIcon /> : <CancelIcon />}
                            label={`${s.promoted ? t('classPromotionSummaryPage.promoted') : t('classPromotionSummaryPage.retained')} ${t('classPromotionSummaryPage.differs', { defaultValue: '(differs from rules)' })}`}
                            color="warning"
                            size="small"
                          />
                        ) : s.promoted ? (
                          <Chip icon={<CheckCircleIcon />} label={t('classPromotionSummaryPage.promoted')} color="success" size="small" />
                        ) : (
                          <Chip icon={<CancelIcon />} label={t('classPromotionSummaryPage.retained')} color="error" size="small" />
                        )}
                        {/* The rules set the default, not the outcome. A head
                            may know something the marks do not, and the
                            alternative to an override is editing enrolments by
                            hand. Offered only where it means something — on a
                            student the rules would hold back. */}
                        {s.promoted && (
                          <Button
                            size="small"
                            color="error"
                            sx={{ ml: 1 }}
                            disabled={overrideBusy || !nextYearId}
                            onClick={() => setHoldStudent(s)}
                          >
                            {t('classPromotionSummaryPage.holdBack', { defaultValue: 'Hold back' })}
                          </Button>
                        )}
                        {!s.promoted && (
                          <Button
                            size="small"
                            sx={{ ml: 1 }}
                            disabled={overrideBusy || !nextYearId || !progressionMap?.[selectedClass]}
                            onClick={() => setOverrideStudent(s)}
                          >
                            {progressionMap?.[selectedClass]
                              ? t('classPromotionSummaryPage.promoteAnyway', { defaultValue: 'Promote anyway' })
                              : t('classPromotionSummaryPage.setNextClassFirst', { defaultValue: 'Set the next class to promote' })}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                    {s.reasons && s.reasons.length > 0 && (
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                          <Collapse in={expandedRows[s.studentId]} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 1, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                              <Typography variant="subtitle2" gutterBottom>{t('classPromotionSummaryPage.criteriaEvaluation')}:</Typography>
                              {s.reasons.map((r, idx) => (
                                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                  {r.passed ? <CheckCircleIcon color="success" sx={{ mr: 1, fontSize: 18 }} /> : <CancelIcon color="error" sx={{ mr: 1, fontSize: 18 }} />}
                                  <Typography variant="body2">
                                    <strong>{r.rule}:</strong> {r.details}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      )}

      {preview && (
        <Paper sx={{ mt: 3, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t('classPromotionSummaryPage.previewResults', { yearId: nextYearId || t('classPromotionSummaryPage.notSet') })}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>{t('classPromotionSummaryPage.totalStudents')}</Typography>
                  <Typography variant="h4">{preview.stats?.total || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: 'success.light' }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>{t('classPromotionSummaryPage.toBePromoted')}</Typography>
                  <Typography variant="h4">{preview.stats?.promoted || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: 'error.light' }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>{t('classPromotionSummaryPage.toBeRetained')}</Typography>
                  <Typography variant="h4">{preview.stats?.retained || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 260 }}>
              <InputLabel id="next-class-label">{t('classPromotionSummaryPage.nextClassForPromoted')}</InputLabel>
              <Select
                labelId="next-class-label"
                value={progressionMap[selectedClass] ?? nextClassId}
                label={t('classPromotionSummaryPage.nextClassForPromoted')}
                onChange={(e) => {
                  setNextClassId(e.target.value);
                  handleMapChange(selectedClass, e.target.value);
                }}
              >
                <MenuItem value=""><em>{t('classPromotionSummaryPage.keepSameClass')}</em></MenuItem>
                {classes.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button variant="contained" color="success" onClick={handleOpenConfirm} disabled={execBusy || !nextYearId}>
              {execBusy ? <CircularProgress size={20} /> : t('classPromotionSummaryPage.executePromotions')}
            </Button>
          </Box>

          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={50} />
                  <TableCell>{t('classPromotionSummaryPage.columns.student')}</TableCell>
                  <TableCell>{t('classPromotionSummaryPage.columns.overallAvg')}</TableCell>
                  <TableCell>{t('classPromotionSummaryPage.columns.decision')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.results.map((r) => (
                  <React.Fragment key={r.studentId}>
                    <TableRow sx={{ '& > *': { borderBottom: expandedRows[`prev_${r.studentId}`] ? 'unset !important' : undefined } }}>
                      <TableCell>
                        {r.reasons && r.reasons.length > 0 && (
                          <IconButton
                            size="small"
                            onClick={() => setExpandedRows(prev => ({ ...prev, [`prev_${r.studentId}`]: !prev[`prev_${r.studentId}`] }))}
                          >
                            {expandedRows[`prev_${r.studentId}`] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar src={r.photoUrl || ''} sx={{ width: 36, height: 36 }}>
                            {(r.studentName || '').charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography variant="body2">{r.studentName}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell><strong>{r.overallAverage?.toFixed(2)}</strong></TableCell>
                      <TableCell>
                        {r.promoted ? (
                          <Chip icon={<CheckCircleIcon />} label={t('classPromotionSummaryPage.promoted')} color="success" size="small" />
                        ) : (
                          <Chip icon={<CancelIcon />} label={t('classPromotionSummaryPage.retained')} color="error" size="small" />
                        )}
                      </TableCell>
                    </TableRow>
                    {r.reasons && r.reasons.length > 0 && (
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
                          <Collapse in={expandedRows[`prev_${r.studentId}`]} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 1, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                              <Typography variant="subtitle2" gutterBottom>{t('classPromotionSummaryPage.criteriaEvaluation')}:</Typography>
                              {r.reasons.map((reason, idx) => (
                                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                  {reason.passed ? <CheckCircleIcon color="success" sx={{ mr: 1, fontSize: 18 }} /> : <CancelIcon color="error" sx={{ mr: 1, fontSize: 18 }} />}
                                  <Typography variant="body2">
                                    <strong>{reason.rule}:</strong> {reason.details}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      )}
      </>
      )}

      {tabIndex === 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>{t('classPromotionSummaryPage.defineProgressions')}</Typography>
          <Typography sx={{ mb: 2 }}>{t('classPromotionSummaryPage.defineProgressionsDesc')}</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('classPromotionSummaryPage.columns.currentClass')}</TableCell>
                <TableCell>{t('classPromotionSummaryPage.columns.nextClass')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {classes.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>{c.name}</TableCell>
                  <TableCell>
                    <FormControl sx={{ minWidth: 240 }}>
                      <InputLabel id={`map-${c.id}`}>{t('classPromotionSummaryPage.columns.nextClass')}</InputLabel>
                      <Select
                        labelId={`map-${c.id}`}
                        value={progressionMap[c.id] ?? ''}
                        label={t('classPromotionSummaryPage.columns.nextClass')}
                        onChange={(e) => handleMapChange(c.id, e.target.value)}
                      >
                        <MenuItem value=""><em>{t('classPromotionSummaryPage.keepSame')}</em></MenuItem>
                        {classes.map((opt) => (
                          <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={handleSaveMapping} disabled={mapSaving}>
              {mapSaving ? <CircularProgress size={20} /> : t('classPromotionSummaryPage.saveMappings')}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Confirm Execution Dialog */}
      <Dialog open={Boolean(holdStudent)} onClose={() => !overrideBusy && setHoldStudent(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('classPromotionSummaryPage.holdBack', { defaultValue: 'Hold back' })}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('classPromotionSummaryPage.holdBody', {
              name: holdStudent?.studentName,
              defaultValue: `${holdStudent?.studentName || ''} will repeat the same class instead of moving up. This is recorded against your name.`,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHoldStudent(null)} disabled={overrideBusy}>{t('common.cancel')}</Button>
          <Button variant="contained" color="error" onClick={handleHoldBack} disabled={overrideBusy}>
            {t('classPromotionSummaryPage.holdBack', { defaultValue: 'Hold back' })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(overrideStudent)} onClose={() => !overrideBusy && setOverrideStudent(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('classPromotionSummaryPage.promoteAnyway', { defaultValue: 'Promote anyway' })}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('classPromotionSummaryPage.overrideBody', {
              name: overrideStudent?.studentName,
              defaultValue: `${overrideStudent?.studentName || ''} does not meet the promotion rules. Promoting anyway enrols them into the selected year as promoted, and is recorded against your name with the reasons they failed.`,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideStudent(null)} disabled={overrideBusy}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handlePromoteAnyway} disabled={overrideBusy}>
            {t('classPromotionSummaryPage.promoteAnyway', { defaultValue: 'Promote anyway' })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={handleCloseConfirm} maxWidth="sm" fullWidth>
        <DialogTitle>{t('classPromotionSummaryPage.dialogs.confirm.title')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('classPromotionSummaryPage.dialogs.confirm.warning')}
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}><strong>{t('classPromotionSummaryPage.dialogs.confirm.academicYearId')}:</strong> {nextYearId || t('classPromotionSummaryPage.notSet')}</Typography>
          {preview && (
            <Box sx={{ my: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}><strong>{t('classPromotionSummaryPage.totalStudents')}:</strong> {preview.stats?.total || 0}</Typography>
              <Typography variant="body2" sx={{ mb: 1, color: 'success.main' }}><strong>{t('classPromotionSummaryPage.toBePromoted')}:</strong> {preview.stats?.promoted || 0}</Typography>
              <Typography variant="body2" sx={{ mb: 1, color: 'error.main' }}><strong>{t('classPromotionSummaryPage.toBeRetained')}:</strong> {preview.stats?.retained || 0}</Typography>
            </Box>
          )}
          {!!(progressionMap[selectedClass] || nextClassId) && (
            <Typography variant="body2" sx={{ mt: 2, p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
              <strong>{t('classPromotionSummaryPage.dialogs.confirm.destinationClass')}:</strong> {(() => {
                const id = progressionMap[selectedClass] ?? nextClassId;
                const found = classes.find(c => String(c.id) === String(id));
                return found ? found.name : id;
              })()}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm} disabled={execBusy}>{t('common.cancel')}</Button>
          <Button variant="contained" color="success" onClick={handleConfirmExecute} disabled={execBusy}>
            {execBusy ? <CircularProgress size={20} /> : t('classPromotionSummaryPage.dialogs.confirm.confirmExecute')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
